import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../components/Icon';
import { BotBadge } from '../components/ui';
import { initial, isOnline, presenceLabel } from '../lib/format';

export default function UserProfileScreen({ person, onBack, onMessage }) {
  const [slide, setSlide] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const gallery = useMemo(() => {
    const all = [person.profileImage, ...(person.photos || [])].filter(Boolean);
    return Array.from(new Set(all));
  }, [person]);

  const photos = person.photos || [];
  const current = gallery[slide];

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowLeft') setLightbox((i) => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setLightbox((i) => Math.min(photos.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightbox, photos.length]);

  return (
    <div className="screen">
      <div className="viewed-hero">
        <button type="button" className="icon-btn viewed-back" onClick={onBack} aria-label="Go back">
          <Icon name="back" size={19} />
        </button>

        {current ? (
          <img src={current} alt={`${person.username}, photo ${slide + 1}`} />
        ) : (
          <div className="viewed-hero-fallback">{initial(person.username)}</div>
        )}

        {gallery.length > 1 && (
          <>
            <div className="carousel-dots">
              {gallery.map((src, index) => (
                <button
                  type="button"
                  key={src}
                  className={`carousel-dot${index === slide ? ' active' : ''}`}
                  onClick={() => setSlide(index)}
                  aria-label={`Show photo ${index + 1}`}
                />
              ))}
            </div>
            {slide > 0 && (
              <button
                type="button"
                className="carousel-nav carousel-prev"
                onClick={() => setSlide((i) => i - 1)}
                aria-label="Previous photo"
              >
                <Icon name="chevronLeft" size={20} />
              </button>
            )}
            {slide < gallery.length - 1 && (
              <button
                type="button"
                className="carousel-nav carousel-next"
                onClick={() => setSlide((i) => i + 1)}
                aria-label="Next photo"
              >
                <Icon name="chevronRight" size={20} />
              </button>
            )}
          </>
        )}

        <div className="viewed-hero-scrim" />
        <div className="viewed-hero-info">
          <h1 className="viewed-hero-name">
            {person.username}
            {person.isBot && <BotBadge />}
          </h1>
          <span className="presence" style={{ marginTop: 4 }}>
            <span className={`presence-dot${isOnline(person.lastSeen) ? ' online' : ''}`} />
            {presenceLabel(person.lastSeen)}
          </span>
        </div>
      </div>

      <div className="screen-body screen-body-full" style={{ paddingTop: 16 }}>
        <div className="stack">
          <section className="card">
            <div className="card-head">
              <span className="section-label">About</span>
            </div>
            <p className={`bio-text${person.bio ? '' : ' bio-empty'}`}>
              {person.bio || `${person.username} hasn't written a bio yet.`}
            </p>
          </section>

          {photos.length > 0 && (
            <section className="card">
              <div className="card-head">
                <span className="section-label">Photos · {photos.length}</span>
              </div>
              <div className="photo-grid">
                {photos.map((photo, index) => (
                  <button
                    type="button"
                    className="photo-slot"
                    key={photo}
                    onClick={() => setLightbox(index)}
                    style={{ border: 'none', padding: 0, cursor: 'zoom-in' }}
                    aria-label={`Open photo ${index + 1} full size`}
                  >
                    <img src={photo} alt={`${person.username}, photo ${index + 1}`} />
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      <div className="action-bar">
        <div className="action-bar-inner">
          <button type="button" className="btn btn-gradient btn-lg btn-block" onClick={() => onMessage(person)}>
            <Icon name="chat" size={18} />
            Message {person.username}
          </button>
        </div>
      </div>

      {lightbox !== null && photos[lightbox] && (
        <div className="lightbox" role="dialog" aria-modal="true" onClick={() => setLightbox(null)}>
          <img src={photos[lightbox]} alt={`${person.username}, photo ${lightbox + 1}`} />
          <button type="button" className="icon-btn lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">
            <Icon name="x" size={20} />
          </button>
          {lightbox > 0 && (
            <button
              type="button"
              className="carousel-nav carousel-prev"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(lightbox - 1);
              }}
              aria-label="Previous photo"
            >
              <Icon name="chevronLeft" size={20} />
            </button>
          )}
          {lightbox < photos.length - 1 && (
            <button
              type="button"
              className="carousel-nav carousel-next"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(lightbox + 1);
              }}
              aria-label="Next photo"
            >
              <Icon name="chevronRight" size={20} />
            </button>
          )}
          <div className="lightbox-counter">
            {lightbox + 1} of {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
